import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Package, Leaf, Clock, ThermometerSnowflake, Droplets } from 'lucide-react';

const FresherMat = () => {
  const [activeQuestion, setActiveQuestion] = useState(null);

  const faqs = [
    {
      question: "How does FresherMat work?",
      answer: "FresherMat uses frequency programming technology embedded in vinyl-faced magnetic strips. When positioned 30cm apart, they create an optimized preservation zone that helps water molecules in produce maintain their structure, naturally retaining moisture and extending freshness."
    },
    {
      question: "What foods does it work with?",
      answer: "FresherMat works with most fresh produce including fruits (bananas, apples, tomatoes), vegetables, salads, and herbs. Our testing shows particularly strong results with bananas, tomatoes, leafy greens, and herbs. Results may vary by produce type and storage conditions."
    },
    {
      question: "How do I install it?",
      answer: "Installation is incredibly simple: 1) Cut the flexible strips to match your shelf size using scissors, 2) Position the two strips exactly 30cm apart on any shelf, fridge, freezer, or storage box, 3) Place your produce in the preservation zone. No tools or professional installation required."
    },
    {
      question: "How do I clean FresherMat?",
      answer: "Simply wipe the strips with a damp cloth. The vinyl-faced surface is easy to clean and maintain. No special cleaning products needed."
    },
    {
      question: "What's the warranty?",
      answer: "FresherMat comes with a 10-year lifespan guarantee. The frequency programming technology is embedded permanently and doesn't degrade over time. Install once and enjoy a decade of food preservation."
    },
    {
      question: "Do you offer bulk pricing for businesses?",
      answer: "Yes! We offer custom pricing for hotels, restaurants, supermarkets, and other commercial clients. Contact us at info@full-bin.com or call +44 20 3996 2569 for bulk quotes and partnership opportunities."
    }
  ];

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero hero-with-image">
        <div className="hero-background-image">
          <img 
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHByb2R1Y2V8ZW58MHx8fHwxNzMzNjAyNDQxfDA&ixlib=rb-4.1.0&q=85"
            alt="Fresh produce"
            loading="eager"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">Revolutionary Preservation Technology</div>
            <h1 className="heading-1">Keep Food Fresher, Longer</h1>
            <p className="body-large">
              Frequency-programmed magnetic strips that help keep produce fresh for days longer. Used by hotels, restaurants and supermarkets. No chemicals. Just science.
            </p>
            <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '25px'}}>
              <a href="https://buy.stripe.com/8x23co2UE7C27P61l96c00f?locale=en-GB" target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 28px',
                background: '#00897b',
                color: 'white',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Shop Now - £550
                <ArrowRight size={18} />
              </a>
              <a href="#how-it-works" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 28px',
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="section" style={{padding: '40px 0', background: '#f5f5f5'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', maxWidth: '900px', margin: '0 auto'}}>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>3-5</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>Days Longer Freshness</div>
            </div>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>10</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>Year Lifespan</div>
            </div>
            <div style={{textAlign: 'center', padding: '25px', background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
              <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '8px'}}>£550</div>
              <div style={{fontSize: '15px', color: '#555', fontWeight: '500'}}>Per System</div>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Real Results</h2>
            <p className="section-subtitle">
              Watch how bananas and tomatoes stay fresh over 5 days with FresherMat protection
            </p>
          </div>

          <div style={{maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px'}}>
            <div style={{background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)'}}>
              <img 
                src="https://customer-assets.emergentagent.com/job_shelf-strip-solution/artifacts/sowk3e3v_Ska%CC%88rmavbild%202025-12-27%20kl.%2018.25.43.png"
                alt="Day 1: Fresh banana and tomato on FresherMat strips"
                style={{width: '100%', borderRadius: '8px', marginBottom: '20px'}}
              />
              <div style={{fontSize: '24px', fontWeight: '700', marginBottom: '10px', color: '#1a1a1a'}}>Day 1</div>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '10px', color: '#00897b'}}>Fresh Start</h3>
              <p style={{fontSize: '15px', color: '#555', lineHeight: '1.6'}}>
                Fresh banana and tomato placed on FresherMat strips, positioned 30cm apart. The preservation zone is now active.
              </p>
            </div>

            <div style={{background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '2px solid #00897b'}}>
              <img 
                src="https://customer-assets.emergentagent.com/job_shelf-strip-solution/artifacts/t0uxld2l_Ska%CC%88rmavbild%202025-12-27%20kl.%2018.26.11.png"
                alt="Day 5: Same banana and tomato still fresh"
                style={{width: '100%', borderRadius: '8px', marginBottom: '20px'}}
              />
              <div style={{fontSize: '24px', fontWeight: '700', marginBottom: '10px', color: '#1a1a1a'}}>Day 5</div>
              <h3 style={{fontSize: '20px', fontWeight: '600', marginBottom: '10px', color: '#00897b'}}>Still Fresh!</h3>
              <p style={{fontSize: '15px', color: '#555', lineHeight: '1.6'}}>
                The same banana and tomato, 5 days later. Thanks to FresherMat's frequency programming technology, the produce has retained its freshness and moisture.
              </p>
            </div>
          </div>

          <div style={{textAlign: 'center', marginTop: '40px'}}>
            <p style={{fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '10px'}}>
              No chemicals. No additives. Just frequency programming technology at work.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">How FresherMat Works</h2>
            <p className="section-subtitle">
              No tools required. No professional installation. Just three simple steps to fresher food.
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px', maxWidth: '1100px', margin: '0 auto'}}>
            <div style={{textAlign: 'center'}}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#00897b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px',
                fontWeight: '700',
                color: 'white'
              }}>1</div>
              <h3 className="heading-3">Cut to Size</h3>
              <p className="body-medium">
                Use scissors to cut the flexible FresherMat strips to match your shelf dimensions.
              </p>
            </div>

            <div style={{textAlign: 'center'}}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#00897b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px',
                fontWeight: '700',
                color: 'white'
              }}>2</div>
              <h3 className="heading-3">Position 30cm Apart</h3>
              <p className="body-medium">
                Place the two strips exactly 30cm apart on any shelf, fridge, freezer, or storage box.
              </p>
            </div>

            <div style={{textAlign: 'center'}}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#00897b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '32px',
                fontWeight: '700',
                color: 'white'
              }}>3</div>
              <h3 className="heading-3">Enjoy Fresh Food</h3>
              <p className="body-medium">
                The preservation zone is active immediately. Your food stays fresh for days longer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Science */}
      <section className="section">
        <div className="container">
          <div style={{maxWidth: '1000px', margin: '0 auto'}}>
            <div className="section-header">
              <h2 className="heading-2">The Science Behind FresherMat</h2>
              <p className="section-subtitle">
                Understanding how frequency programming technology extends the life of your fresh produce
              </p>
            </div>

            <div style={{background: '#f5f5f5', borderRadius: '12px', padding: '40px', marginBottom: '40px'}}>
              <h3 style={{fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a'}}>How Does It Work?</h3>
              <p style={{fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px'}}>
                FresherMat uses frequency programming technology — a sophisticated process that embeds specific frequencies into our vinyl-faced magnetic strips. When two strips are positioned 30cm apart, they create an optimised preservation zone.
              </p>
              <p style={{fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px'}}>
                Within this zone, the technology works at a molecular level to help water molecules in your produce maintain their structure. This helps food retain moisture naturally, which is the key factor in keeping fruits, vegetables, and herbs fresh.
              </p>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '30px'}}>
                <div style={{display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                  <Droplets size={24} color="#00897b" style={{flexShrink: 0, marginTop: '4px'}} />
                  <div>
                    <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '5px'}}>Moisture Retention</h4>
                    <p style={{fontSize: '14px', color: '#555'}}>Helps produce hold onto natural moisture that keeps it crisp and fresh</p>
                  </div>
                </div>

                <div style={{display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                  <Package size={24} color="#00897b" style={{flexShrink: 0, marginTop: '4px'}} />
                  <div>
                    <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '5px'}}>Frequency Technology</h4>
                    <p style={{fontSize: '14px', color: '#555'}}>Programmed frequencies work continuously without electricity</p>
                  </div>
                </div>

                <div style={{display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                  <ThermometerSnowflake size={24} color="#00897b" style={{flexShrink: 0, marginTop: '4px'}} />
                  <div>
                    <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '5px'}}>Wide Temperature Range</h4>
                    <p style={{fontSize: '14px', color: '#555'}}>Effective from -20°C to +80°C — freezer to ambient</p>
                  </div>
                </div>

                <div style={{display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                  <Leaf size={24} color="#00897b" style={{flexShrink: 0, marginTop: '4px'}} />
                  <div>
                    <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '5px'}}>No Direct Contact</h4>
                    <p style={{fontSize: '14px', color: '#555'}}>Strips don't touch food — completely safe and hygienic</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Why Choose FresherMat</h2>
            <p className="section-subtitle">
              Proven technology backed by real-world results from hotels, restaurants, and supermarkets
            </p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px', maxWidth: '1100px', margin: '0 auto'}}>
            <div style={{background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <Clock size={32} color="#00897b" style={{marginBottom: '15px'}} />
              <h3 className="heading-3">Extend Freshness</h3>
              <p className="body-medium">
                Frequency programming helps retain moisture and slow decomposition naturally, keeping produce fresh for days longer.
              </p>
            </div>

            <div style={{background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <Leaf size={32} color="#00897b" style={{marginBottom: '15px'}} />
              <h3 className="heading-3">Reduce Food Waste</h3>
              <p className="body-medium">
                Less spoilage means significant savings and reduced environmental impact for your business.
              </p>
            </div>

            <div style={{background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <Package size={32} color="#00897b" style={{marginBottom: '15px'}} />
              <h3 className="heading-3">10 Year Lifespan</h3>
              <p className="body-medium">
                Install once and forget. No replacement needed for a full decade of preservation.
              </p>
            </div>

            <div style={{background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
              <Check size={32} color="#00897b" style={{marginBottom: '15px'}} />
              <h3 className="heading-3">Zero Chemicals</h3>
              <p className="body-medium">
                No direct food contact. No additives. Just natural preservation through advanced technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section id="product" className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Get Your FresherMat</h2>
            <p className="section-subtitle">
              One purchase includes everything you need to start preserving your food better
            </p>
          </div>

          <div style={{maxWidth: '800px', margin: '0 auto', background: 'white', borderRadius: '16px', padding: '40px', boxShadow: '0 8px 32px rgba(0,0,0,0.1)'}}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center'}}>
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_shelf-strip-solution/artifacts/sowk3e3v_Ska%CC%88rmavbild%202025-12-27%20kl.%2018.25.43.png"
                  alt="FresherMat Complete System"
                  style={{width: '100%', borderRadius: '12px'}}
                />
              </div>

              <div>
                <h3 style={{fontSize: '28px', fontWeight: '700', marginBottom: '15px'}}>FresherMat Complete System</h3>
                <div style={{fontSize: '42px', fontWeight: '700', color: '#00897b', marginBottom: '20px'}}>£550</div>
                
                <div style={{marginBottom: '25px'}}>
                  <p style={{fontSize: '16px', fontWeight: '600', marginBottom: '10px'}}>What's Included:</p>
                  <ul style={{listStyle: 'none', padding: 0}}>
                    {[
                      '2 preservation strips included',
                      '30cm optimal spacing',
                      '10-year lifespan',
                      'No chemicals or additives',
                      'Easy wipe-clean maintenance',
                      'Works -20°C to +80°C'
                    ].map((item, i) => (
                      <li key={i} style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', fontSize: '14px'}}>
                        <Check size={18} color="#00897b" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <a 
                  href="https://buy.stripe.com/8x23co2UE7C27P61l96c00f?locale=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '16px 32px',
                    background: '#00897b',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  Order Now - £550
                </a>
                
                <p style={{fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '15px'}}>
                  For bulk orders, contact us for custom pricing
                </p>
              </div>
            </div>

            <div style={{marginTop: '30px', padding: '20px', background: '#f5f5f5', borderRadius: '8px'}}>
              <p style={{fontSize: '14px', fontWeight: '600', marginBottom: '10px'}}>Perfect for:</p>
              <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                {['Refrigerators', 'Freezers', 'Shelves', 'Storage Boxes'].map(place => (
                  <span key={place} style={{padding: '6px 14px', background: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '500'}}>
                    {place}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Trusted by Industry Leaders</h2>
            <p className="section-subtitle">See what our commercial partners say about FresherMat technology</p>
          </div>

          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px', maxWidth: '1100px', margin: '0 auto'}}>
            {[
              {
                quote: "Our walk-in fridges are performing noticeably better. The herbs and salad ingredients last significantly longer, reducing our daily waste considerably.",
                initial: "H",
                role: "Hotel Kitchen Manager",
                sector: "Hospitality Sector"
              },
              {
                quote: "We were sceptical at first, but the results speak for themselves. Fresh produce stays crisp and vibrant for much longer than before.",
                initial: "R",
                role: "Restaurant Owner",
                sector: "Fine Dining"
              },
              {
                quote: "Brilliant solution for our busy kitchen. Less spoilage means better margins and fresher food for our customers every single day.",
                initial: "P",
                role: "Pub Kitchen Supervisor",
                sector: "Food & Beverage"
              }
            ].map((testimonial, i) => (
              <div key={i} style={{background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}>
                <p style={{fontSize: '15px', fontStyle: 'italic', color: '#333', marginBottom: '20px', lineHeight: '1.7'}}>
                  "{testimonial.quote}"
                </p>
                <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#00897b',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: '700'
                  }}>
                    {testimonial.initial}
                  </div>
                  <div>
                    <div style={{fontSize: '14px', fontWeight: '600', color: '#1a1a1a'}}>{testimonial.role}</div>
                    <div style={{fontSize: '13px', color: '#666'}}>{testimonial.sector}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about FresherMat technology</p>
          </div>

          <div style={{maxWidth: '800px', margin: '0 auto'}}>
            {faqs.map((faq, index) => (
              <div key={index} style={{
                marginBottom: '15px',
                background: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <button
                  onClick={() => setActiveQuestion(activeQuestion === index ? null : index)}
                  style={{
                    width: '100%',
                    padding: '20px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1a1a1a',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {faq.question}
                  <span style={{fontSize: '24px', color: '#00897b'}}>
                    {activeQuestion === index ? '−' : '+'}
                  </span>
                </button>
                {activeQuestion === index && (
                  <div style={{
                    padding: '0 20px 20px 20px',
                    fontSize: '15px',
                    lineHeight: '1.7',
                    color: '#555'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{textAlign: 'center', marginTop: '40px'}}>
            <p style={{fontSize: '16px', marginBottom: '15px'}}>Still have questions? We're here to help!</p>
            <Link to="/contact" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#00897b',
              fontSize: '16px',
              fontWeight: '600',
              textDecoration: 'none'
            }}>
              Contact our team
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Ready to reduce food waste and save money?</h2>
            <p className="body-large">
              Join hotels, restaurants, and supermarkets using FresherMat technology.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Started Today
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FresherMat;
